import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TemplateRef } from '@angular/core';
import { ProductsService } from 'src/app/shared/service/product.service';
import { HttpClient } from '@angular/common/http';
import { APIService } from 'src/app/shared/service/apiService.service';
import Swal from 'sweetalert2';
import { ComponentType } from '@angular/cdk/portal';

@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.scss']
})
export class ProfileComponent implements OnInit, OnDestroy {

  profileImageUrl: string = '';
  public formSubmitted: boolean = false;
  public active = 1;
  public ultimoGradoEstudios: string = '';
  public carrera: string = '';
  public escuela: string = '';
  public aboutMe: string = '';
  public fbUrl: string = '';
  public xUrl: string = '';
  public lnUrl: string = '';
  public nuevoAntecedente = { empresa: '', cargo: '', periodoInicio: '', periodoFin: '' };
  public antecedentes: { id?: number, company: string, position: string, startDate: string, endDate: string, userId?: number }[] = [];
  profile: any;
  Id: any;
  onEdit = false;
  private apiUrl = 'http://localhost:4071/api/UserHistory/Job';
  antecedenteEditandoId: number | null = null;
  dialogTemplate: ComponentType<unknown>;

  // 🚀 OPTIMIZACIÓN SAFARI: Loading states para mejor UX
  isLoadingProfile: boolean = false;
  isLoadingJobHistory: boolean = false;
  isSubmittingForm: boolean = false;

  constructor(
    public dialog: MatDialog,
    private apiService: APIService,
    private productService: ProductsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Obtener ID de usuario del localStorage
    try {
      const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
      this.Id = currentUser?.id ?? null;
    } catch {
      this.Id = null;
    }

    // Cargar perfil y experiencia laboral
    this.loadProfile();
    this.getJobHistory();
  }

  private loadProfile(): void {
    if (!this.Id) return;
    this.isLoadingProfile = true;
    this.cdr.detectChanges();
    
    // Usar la nueva API auth/profile para obtener el perfil con la URL de la imagen
    this.apiService.getProfile().subscribe({
      next: (data: any) => {
        this.profile = data || {};
        
        // Intentar obtener la imagen en este orden de prioridad:
        // 1. profileFileUrl (si es una URL válida)
        // 2. profileImage.base64 (si existe)
        // 3. imgProfile (fallback)
        let imageUrl = this.profile?.profileFileUrl || this.profile?.imgProfile;
        
        // Limpiar la URL si es una ruta del servidor
        imageUrl = this.cleanImageUrl(imageUrl);
        
        // Si no hay URL válida pero hay base64, usar la imagen base64
        if (!imageUrl && this.profile?.profileImage?.base64) {
          imageUrl = `data:image/jpeg;base64,${this.profile.profileImage.base64}`;
        }
        
        // La URL de la imagen viene directamente del backend
        this.profileImageUrl = imageUrl || 'assets/images/user.png';
        
        // Actualizar localStorage PRESERVANDO datos existentes como token
        try {
          const existingData = JSON.parse(localStorage.getItem('currentUser') || '{}');
          
          const userDataForStorage = {
            ...existingData, // Preservar token y otros datos existentes
            ...data, // Actualizar con nuevos datos del perfil
            // NO guardar base64 en localStorage, solo metadatos
            profileImage: data.profileImage ? {
              hasBase64: !!data.profileImage.base64
            } : null
          };
          
          // Solo guardar imgProfile si NO es base64
          if (imageUrl && !imageUrl.startsWith('data:')) {
            userDataForStorage.imgProfile = imageUrl;
            userDataForStorage.profileFileUrl = imageUrl;
          } else {
            // Remover imgProfile con base64 de localStorage
            delete userDataForStorage.imgProfile;
          }
          
          localStorage.setItem('currentUser', JSON.stringify(userDataForStorage));
          
          // Guardar la imagen base64 en sessionStorage (separado)
          if (imageUrl?.startsWith('data:')) {
            try {
              sessionStorage.setItem('profileImageBase64', imageUrl);
            } catch (e) {
              console.warn('⚠️ [PROFILE] No se pudo guardar imagen en sessionStorage:', e);
            }
          }
        } catch (e) {
          console.error('❌ [PROFILE] Error al actualizar localStorage:', e);
        }
        
        this.isLoadingProfile = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.isLoadingProfile = false;
        Swal.fire('Error', 'No se pudo cargar tu perfil', 'error');
        this.cdr.detectChanges();
      }
    });
  }

  // Limpiar URL de imagen del servidor a URL pública
  private cleanImageUrl(url: any): string | null {
    if (!url || typeof url !== 'string') return null;
    
    // Si es solo un número, retornar null
    if (/^\d+(\.\d+)?$/.test(url)) return null;
    
    // Si es una ruta del servidor, retornar null (silenciosamente)
    if (url.startsWith('/') && (url.includes('/var/') || url.includes('/usr/') || url.includes('/home/'))) {
      return null;
    }
    
    // Si ya es una URL válida, retornarla
    if (url.startsWith('http') || url.startsWith('https') || url.startsWith('assets') || url.startsWith('data:image')) {
      return url;
    }
    
    return null;
  }

  getJobHistory(): void {
    if (!this.Id) return;
    this.isLoadingJobHistory = true;
    this.cdr.detectChanges();
    this.productService.getJobHistory(this.Id).subscribe({
      next: (resp: any) => {
        this.antecedentes = Array.isArray(resp) ? resp : (resp?.data || []);
        this.isLoadingJobHistory = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isLoadingJobHistory = false;
        console.error('Error al obtener historial laboral:', error);
        Swal.fire('Error', 'No se pudo obtener el historial laboral', 'error');
        this.cdr.detectChanges();
      }
    });
  }

  // Método para guardar un nuevo antecedente laboral
  guardarAntecedente() {
    if (this.nuevoAntecedente.empresa && this.nuevoAntecedente.cargo && this.nuevoAntecedente.periodoInicio) {
      this.isSubmittingForm = true;
      this.cdr.detectChanges();

      const jobData = {
        company: this.nuevoAntecedente.empresa,
        position: this.nuevoAntecedente.cargo,
        startDate: this.nuevoAntecedente.periodoInicio,
        endDate: this.nuevoAntecedente.periodoFin || null,
        userId: this.Id
      };
  
      this.productService.addJobHistory(jobData).subscribe({
        next: (response) => {
          // 🚀 OPTIMIZACIÓN: Usar requestAnimationFrame para mejor UX
          requestAnimationFrame(() => {
            Swal.fire('Éxito', 'Experiencia laboral guardada correctamente', 'success');
            this.getJobHistory();
            this.nuevoAntecedente = { empresa: '', cargo: '', periodoInicio: '', periodoFin: '' };
            this.dialog.closeAll();
            this.isSubmittingForm = false;
            this.cdr.detectChanges();
          });
        },
        error: (error) => {
          this.isSubmittingForm = false;
          console.error('Error al guardar experiencia laboral:', error);
          Swal.fire('Error', 'No se pudo guardar la experiencia laboral', 'error');
        }
      });
    }
  }

  // Método para actualizar un antecedente laboral
  actualizarAntecedente(antecedente: any) {
    const jobData = {
      id: antecedente.id,
      company: antecedente.company,
      position: antecedente.position,
      startDate: antecedente.startDate,
      endDate: antecedente.endDate || null,
      userId: this.Id
    };

    this.productService.updateJobHistory(jobData).subscribe({
      next: (response: any) => {
        Swal.fire({
          icon: 'success',
          title: 'Éxito',
          text: 'Experiencia laboral actualizada correctamente'
        });
        
        // Actualizar la lista de antecedentes
        this.getJobHistory();
      },
      error: (error) => {
        console.error('Error al actualizar experiencia laboral:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo actualizar la experiencia laboral'
        });
      }
    });
  }

  // Método para eliminar un antecedente laboral
  eliminarAntecedente(id: number) {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede revertir",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    }).then((result) => {
      if (result.isConfirmed) {
        this.productService.deleteJobHistory(id).subscribe({
          next: () => {
            Swal.fire(
              'Eliminado',
              'La experiencia laboral ha sido eliminada.',
              'success'
            );
            
            // Actualizar la lista de antecedentes
            this.getJobHistory();
          },
          error: (error) => {
            console.error('Error al eliminar experiencia laboral:', error);
            Swal.fire(
              'Error',
              'No se pudo eliminar la experiencia laboral.',
              'error'
            );
          }
        });
      }
    });
  }

  // Disparar el input de selección de archivo
  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput.click();
  }

  onSelectImages(event: any) {
    const file: File = event?.target?.files?.[0];
    if (!file || !this.Id) return;

    // Validación previa (formatos permitidos y tamaño <= 5MB)
    const v = this.validateProfileImageFile(file);
    if (!v.valid) {
      Swal.fire({ icon: 'error', title: 'Archivo inválido', text: v.error || 'Formato o tamaño no permitido' });
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    formData.append('photo', file); // Cambio de 'file' a 'photo' para la nueva API

    Swal.fire({ title: 'Subiendo imagen...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    
    // Usar la nueva API auth/profile/photo
    this.productService.uploadProfilePhoto(formData).subscribe({
      next: (response: any) => {
        Swal.close();
        
        // El backend devuelve la URL de la imagen directamente
        const imageUrl = response?.url || response?.profileFileUrl || response?.ImageUrl;
        if (!imageUrl) {
          Swal.fire({ icon: 'error', title: 'Error', text: 'No se recibió URL de imagen' });
          return;
        }

        // Actualizar la imagen en la vista
        this.profileImageUrl = imageUrl;

        // Actualizar localStorage PRESERVANDO datos existentes como token
        try {
          const existingData = JSON.parse(localStorage.getItem('currentUser') || '{}');
          
          const userDataForStorage = {
            ...existingData // Preservar token y otros datos existentes
          };
          
          // Solo guardar si NO es base64
          if (!imageUrl.startsWith('data:')) {
            userDataForStorage.imgProfile = imageUrl;
            userDataForStorage.profileFileUrl = imageUrl;
          } else {
            // Si es base64, solo guardar metadatos
            delete userDataForStorage.imgProfile;
            userDataForStorage.profileImage = { hasBase64: true };
            
            // Guardar imagen en sessionStorage
            try {
              sessionStorage.setItem('profileImageBase64', imageUrl);
            } catch (e) {
              console.warn('⚠️ [PROFILE-UPLOAD] No se pudo guardar imagen en sessionStorage:', e);
            }
          }
          
          localStorage.setItem('currentUser', JSON.stringify(userDataForStorage));
        } catch (e) {
          console.error('❌ [PROFILE-UPLOAD] Error al actualizar localStorage:', e);
        }

        // Recargar el perfil para asegurarnos de tener datos actualizados
        this.loadProfile();

        Swal.fire({ icon: 'success', title: 'Éxito', text: 'La imagen de perfil se ha actualizado correctamente' });
      },
      error: (err) => {
        Swal.close();
        const msg = err?.message || 'No se pudo subir la imagen';
        Swal.fire({ icon: 'error', title: 'Error', text: msg });
      }
    });
  }

  // Validación local de imagen (similar a studentprofilemain)
  private validateProfileImageFile(file: File): { valid: boolean; error?: string } {
    try {
      if (!(file instanceof File)) return { valid: false, error: 'Archivo inválido.' };
      const maxBytes = 5 * 1024 * 1024; // 5MB
      const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      const mime = (file.type || '').toLowerCase();
      const name = (file.name || '').toLowerCase();
      const extAllowed = ['.jpg', '.jpeg', '.png', '.webp'];
      const hasAllowedMime = allowed.includes(mime);
      const hasAllowedExt = extAllowed.some(e => name.endsWith(e));
      if (!hasAllowedMime && !hasAllowedExt) return { valid: false, error: 'Formato no permitido. Usa JPG, PNG o WEBP.' };
      if (file.size > maxBytes) return { valid: false, error: 'La imagen supera el límite de 5 MB.' };
      return { valid: true };
    } catch {
      return { valid: false, error: 'No fue posible validar la imagen.' };
    }
  }

  editProfile(data) {
    // Envía los datos usando el servicio
    this.productService.updateProfile(this.Id, data).subscribe(
      response => {
        console.log('Datos enviados correctamente:', response);
        this.formSubmitted = true; // Cambia el estado cuando el formulario se envía correctamente
      },
      error => {
        console.error('Error al enviar los datos:', error);
        this.formSubmitted = false; // Mantener false en caso de error
      }
    );
  }

  saveEducation() {
    const body = {
      grado: this.profile.grado,
      carrera: this.profile.carrera,
      escuela: this.profile.escuela
    }

    this.editProfile(body);
  }

  saveProfile() {
    if (!this.onEdit) {
      this.onEdit = true;
      return;
    }

    const body = {
      firstName: this.profile.firstName,
      lastName: this.profile.lastName,
      displayName: `${this.profile.firstName} ${this.profile.lastName}`,
      email: this.profile.email,
      phoneNumber: this.profile.phoneNumber,
      biography: this.profile.biography
    }

    this.editProfile(body);
    this.onEdit = false;
  }

  // Función que se activa cuando se selecciona un archivo
  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();

      // Esta función se ejecuta cuando el archivo ha sido leído
      reader.onload = (e: any) => {
        // Actualiza la URL de la imagen de perfil con la nueva imagen
        this.profileImageUrl = e.target.result;

        // Guardar la imagen en localStorage
        localStorage.setItem('profileImage', e.target.result);
      };

      // Leer el archivo seleccionado como Data URL (base64)
      reader.readAsDataURL(file);
    }
  }

  mostrarFormulario(templateRef: TemplateRef<any>): void {
    const dialogRef = this.dialog.open(templateRef, {
      width: '500px',
      position: { top: '100px' },
      panelClass: 'custom-dialog'
    });

    dialogRef.afterClosed().subscribe(result => {

    });
  }

  checkCharacterLimit() {
    if (this.aboutMe.length >= 300) {
      this.aboutMe = this.aboutMe.substring(0, 300);
    }
  }

  enviarDatosPerfil() {
    const data = {
      ultimoGradoEstudios: this.ultimoGradoEstudios,
      carrera: this.carrera,
      escuela: this.escuela,
      aboutMe: this.aboutMe,
      antecedentes: this.antecedentes,
      fbUrl: this.fbUrl,
      xUrl: this.xUrl,
      lnUrl: this.lnUrl,
      periodoInicio: this.nuevoAntecedente.periodoInicio,
      periodoFin: this.nuevoAntecedente.periodoFin
    };

    this.editProfile(data);
  }

  mostrarFormularioEdicion(antecedente: any): void {
    // Crear un objeto temporal para editar
    this.nuevoAntecedente = {
      empresa: antecedente.company,
      cargo: antecedente.position,
      periodoInicio: antecedente.startDate.split('T')[0], // Formatear la fecha
      periodoFin: antecedente.endDate ? antecedente.endDate.split('T')[0] : ''
    };
    
    // Guardar el ID para actualizar
    this.antecedenteEditandoId = antecedente.id;
    
    // Mostrar el diálogo de edición
    const dialogRef = this.dialog.open(this.editarAntecedenteDialog, {
      width: '500px',
      disableClose: true
    });

    dialogRef.afterClosed().subscribe(result => {
      // Limpiar el formulario
      this.nuevoAntecedente = { empresa: '', cargo: '', periodoInicio: '', periodoFin: '' };
      this.antecedenteEditandoId = null;
    });
  }
  @ViewChild('editarAntecedenteDialog') editarAntecedenteDialog!: TemplateRef<any>;

  // Agregar método para guardar la edición
  guardarEdicion(): void {
    if (this.antecedenteEditandoId) {
      const antecedenteActualizado = {
        id: this.antecedenteEditandoId,
        company: this.nuevoAntecedente.empresa,
        position: this.nuevoAntecedente.cargo,
        startDate: this.nuevoAntecedente.periodoInicio,
        endDate: this.nuevoAntecedente.periodoFin || null,
        userId: this.Id
      };

      this.productService.updateJobHistory(antecedenteActualizado).subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: 'Éxito',
            text: 'Experiencia laboral actualizada correctamente'
          });
          this.getJobHistory();
          this.dialog.closeAll();
        },
        error: (error) => {
          console.error('Error al actualizar experiencia laboral:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo actualizar la experiencia laboral'
          });
        }
      });
    }
  }

  // 🚀 OPTIMIZACIÓN SAFARI: Cleanup para prevenir memory leaks
  ngOnDestroy(): void {
    // Cerrar diálogos activos
    if (this.dialog) {
      this.dialog.closeAll();
    }

    console.log('🧹 [CLEANUP] Profile component - cleanup completado');
  }
}